import {useEffect, useRef, useState, type FC} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {useAuth} from '../../../../app/modules/auth/core/Auth'
import {
  useLazyGetUserByTokenQuery,
  useSsoLoginMutation,
  useSsoVerifyMutation,
} from '../../../../services/authServiceApi'
import RoleSelectModal, {type SsoRoleOption} from './RoleSelectModal'

const INVALID_LINK = 'SSO sign-in failed. The link is invalid or has expired.'

// Landing page for HR portal SSO redirects: /demo-hub/access/:token
const SsoAccess: FC = () => {
  const navigate = useNavigate()
  const {token} = useParams()
  const {saveAuth, setCurrentUser} = useAuth()
  const [ssoVerify] = useSsoVerifyMutation()
  const [ssoLogin] = useSsoLoginMutation()
  const [getUserByToken] = useLazyGetUserByTokenQuery()

  const [ticket, setTicket] = useState<string | null>(null)
  const [roles, setRoles] = useState<SsoRoleOption[]>([])
  const [pendingRole, setPendingRole] = useState<string | null>(null)
  const didVerify = useRef(false)

  const failToSignIn = (message?: string) => {
    toast.error(message || INVALID_LINK)
    navigate('/auth', {replace: true})
  }

  useEffect(() => {
    // Guard against StrictMode double-invocation of effects.
    if (didVerify.current) return
    didVerify.current = true

    if (!token) {
      failToSignIn()
      return
    }

    ssoVerify(token)
      .unwrap()
      .then((res: {ticket: string; roles?: SsoRoleOption[]}) => {
        setTicket(res.ticket)
        setRoles(res.roles ?? [])
      })
      .catch((error: any) => failToSignIn(error?.data?.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const handleSelect = async (role: string) => {
    if (!ticket) return
    setPendingRole(role)
    try {
      const payload = await ssoLogin({ticket, role}).unwrap()
      saveAuth(payload)
      const user = await getUserByToken(payload.access_token).unwrap()
      setCurrentUser(user)
      toast.success('Signed in successfully')
      navigate('/', {replace: true})
    } catch (error: any) {
      setPendingRole(null)
      failToSignIn(error?.data?.message)
    }
  }

  return (
    <div className='d-flex vh-100 align-items-center justify-content-center'>
      {!ticket && (
        <div className='d-flex align-items-center gap-2 text-muted'>
          <Spinner animation='border' size='sm' />
          Verifying your HR portal access...
        </div>
      )}
      <RoleSelectModal
        show={!!ticket}
        roles={roles}
        pendingRole={pendingRole}
        onSelect={handleSelect}
        onCancel={() => navigate('/auth', {replace: true})}
      />
    </div>
  )
}

export default SsoAccess
